import { useEffect } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, set, onDisconnect, onValue } from 'firebase/database';
import { db, database } from '../firebase';
import { useAuth } from './useAuth';

export function useUserPresence() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user || !user.uid) return;

    // Detectar plataforma de conexión
    const ua = navigator.userAgent || '';
    const isCapacitor = Boolean(window.Capacitor || window.cordova);
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    
    let platform = 'Página Web (Desktop)';
    if (isCapacitor) {
      platform = 'App Móvil Vortex (Nativa)';
    } else if (isMobileUA) {
      platform = 'Página Web (Móvil)';
    }

    const userId = user.uid;
    const now = Date.now();

    const userInfo = {
      uid: userId,
      email: user.email || 'Usuario Anónimo',
      displayName: user.displayName || user.email?.split('@')[0] || 'Aspirante',
      platform,
      userAgent: ua,
      online: true,
      currentRoute: window.location.pathname || '/dashboard',
      lastSeen: now,
      updatedAt: now
    };

    // 1. Actualizar presencia en Firestore ('presence' collection)
    const updateFirestorePresence = async (isOnline = true) => {
      if (!db) return;
      try {
        const presenceDocRef = doc(db, 'presence', userId);
        await setDoc(presenceDocRef, {
          ...userInfo,
          online: isOnline,
          currentRoute: window.location.pathname || '/dashboard',
          lastSeen: Date.now(),
          lastSeenServer: serverTimestamp()
        }, { merge: true });
      } catch (err) {
        // Capturar y silenciar posibles errores de reglas para no afectar la app
      }
    };

    // Actualización inicial
    updateFirestorePresence(true);

    // 2. Intentar presencia opcional en RTDB en la ruta propia del usuario (usersweb/{userId}/presence)
    let unsubscribeConnected = null;
    let rtdbPresenceRef = null;

    if (database) {
      try {
        rtdbPresenceRef = ref(database, `usersweb/${userId}/presence`);
        const connectedRef = ref(database, '.info/connected');

        unsubscribeConnected = onValue(connectedRef, (snap) => {
          if (snap.val() === true && rtdbPresenceRef) {
            onDisconnect(rtdbPresenceRef).set({
              ...userInfo,
              online: false,
              lastSeen: Date.now()
            }).catch(() => {});

            set(rtdbPresenceRef, {
              ...userInfo,
              online: true,
              lastSeen: Date.now()
            }).catch(() => {});
          }
        }, () => {});
      } catch (e) {
        // Silenciar si RTDB no está disponible
      }
    }

    // Heartbeat periódico cada 25 segundos
    const interval = setInterval(() => {
      updateFirestorePresence(true);
      if (rtdbPresenceRef) {
        set(rtdbPresenceRef, {
          ...userInfo,
          online: true,
          currentRoute: window.location.pathname || '/dashboard',
          lastSeen: Date.now()
        }).catch(() => {});
      }
    }, 25000);

    // Eventos de navegación y cierre
    const handleLocationChange = () => {
      updateFirestorePresence(true);
    };

    const handleBeforeUnload = () => {
      updateFirestorePresence(false);
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (typeof unsubscribeConnected === 'function') {
        try { unsubscribeConnected(); } catch (e) {}
      }
      clearInterval(interval);
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      updateFirestorePresence(false);
      if (rtdbPresenceRef) {
        set(rtdbPresenceRef, {
          ...userInfo,
          online: false,
          lastSeen: Date.now()
        }).catch(() => {});
      }
    };
  }, [user]);
}
